import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";

import MainContainer from "../../components/container/MainContainer";
import {
  editRolePermissions,
  getRole,
  getRolePermissions,
  unsetRolePermissions,
} from "../../features/rolesSlice";
import Loading from "../../components/loading/Loading";
import ToggleSwitch from "../../components/form/buttons/ToggleSwitch"; // Import the ToggleSwitch component
import {
  convertToOriginalArrayFormat,
  groupByResource,
  mergeArraysByNameAndAction,
} from "../../util";
import Modal from "../../components/modal/Modal";
import { openModal } from "../../features/modalSLice";
import AddRole from "../roles/AddRole";
import DeleteRole from "../roles/DeleteRole";
import { useNotification } from "../../components/notification/NotificationContainer";
import { Save, Trash2 } from "lucide-react";
import loadStatus from "../../util/loadStatus";
import NetworkError from "../error/NetworkError";

const Permission = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const addNotification = useNotification();
  const initPermissions = useSelector((state) => state.config.permissions);
  const rawPermissions = useSelector((state) => state.config.rawPermissions);
  const permissions = useSelector((state) => state.roles.permissions);
  const role = useSelector((state) => state.roles.selected);
  const modalContent = useSelector((state) => state.modal.content);
  const rolePermLoad = useSelector(
    (state) => state.roles.load.getRolePermissions
  );
  const editRolePermLoad = useSelector(
    (state) => state.roles.load.editRolePermissions
  );
  const roleLoad = useSelector((state) => state.roles.load.getRole);
  const { id } = useParams();

  const { control, handleSubmit, watch, setValue, reset } = useForm({
    defaultValues: groupByResource(initPermissions, permissions),
  });

  let watchAllFields = [];
  watchAllFields = watch();

  const onSubmit = async (data) => {
    try {
      const newData = convertToOriginalArrayFormat(data);
      const finalData = mergeArraysByNameAndAction(rawPermissions, newData);
      //Edit permissions
      const res = await dispatch(
        editRolePermissions({ id: id, data: finalData })
      );
      if (editRolePermissions.fulfilled.match(res)) {
        addNotification("Permissions updated Successfully", "success");
        navigate("/settings/roles");
      } else {
        addNotification(res.payload.message, "error");
      }
    } catch (error) {
      addNotification(error.message, "success");
    }
  };

  //Edit Role
  const openEditRoleModal = (edit, id) => {
    dispatch(openModal(<AddRole edit id={role.id} />));
  };

  //Delete Role
  const openDeleteRoleModal = (id) => {
    dispatch(openModal(<DeleteRole id={role.id} />));
  };

  useEffect(() => {
    dispatch(getRolePermissions(id));
    dispatch(getRole(id));
    return () => {
      dispatch(unsetRolePermissions());
    };
  }, []);

  useEffect(() => {
    // Update form default values once permissions and initial permissions are available
    if (initPermissions.length > 0 && permissions.length > 0) {
      const groupedPermissions = groupByResource(initPermissions, permissions);
      reset(groupedPermissions); // Reset the form with the new default values
    }
  }, [initPermissions, permissions, reset]);

  useEffect(() => {}, [watchAllFields]);

  if (roleLoad === loadStatus.loading || rolePermLoad === loadStatus.loading) {
    return <Loading />;
  }

  if (roleLoad === loadStatus.error || rolePermLoad === loadStatus.error) {
    return <NetworkError to={location.pathname} />;
  }

  return (
    <MainContainer>
      <Modal>{modalContent}</Modal>
      <h3 className="text-text-1/95 mb-6">{role.name}</h3>
      <div className="flex py-4 gap-3">
        <button
          className="add-rss-btn"
          onClick={openEditRoleModal}
          disabled={!role.editable}
        >
          <Save size={18} />
          Edit Role
        </button>
        <button
          className="delete-rss-btn"
          onClick={openDeleteRoleModal}
          disabled={!role.editable}
        >
          <Trash2 size={18} />
          Delete Role
        </button>
      </div>
      <form className="table-container" onSubmit={handleSubmit(onSubmit)}>
        <table>
          <thead className="text-text-gen w-full sticky top-0 z-[1] text-center">
            <tr>
              <th className=" px-4 bg-primary/60  py-2">Resource</th>
              <th className=" px-4 bg-primary/60 text-center py-2">Read</th>
              <th className=" px-4 bg-primary/60 text-center py-2">Create</th>
              <th className=" px-4 bg-primary/60 text-center py-2">Update</th>
              <th className=" px-4 bg-primary/60 text-center py-2">Delete</th>
            </tr>
          </thead>
          <tbody>
            {initPermissions.map(({ name, label }) => (
              <tr key={name}>
                <td className="text-text-1/75 px-4 py-2">{label}</td>
                {["read", "create", "update", "delete"].map((action) => (
                  <td key={action} className=" px-4 py-2 text-center">
                    <Controller
                      name={`${name}.${action}`}
                      control={control}
                      render={({ field }) => (
                        <ToggleSwitch
                          checked={field.value || false}
                          onChange={(e) => {
                            if (action === "read") {
                              setValue(`${name}.${action}`, e.target.checked);
                              setValue(`${name}.update`, e.target.checked);
                              setValue(`${name}.create`, e.target.checked);
                              setValue(`${name}.delete`, e.target.checked);
                            } else if (
                              action !== "read" &&
                              watchAllFields[name].read === false
                            ) {
                            } else
                              setValue(`${name}.${action}`, e.target.checked);
                          }}
                          name={`${name}.${action}`}
                        />
                      )}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <button
          type="submit"
          disabled={editRolePermLoad}
          className="mt-7 bg-primary/75 text-text-gen px-4 py-2 rounded disabled:bg-slate-400"
        >
          Submit
        </button>
      </form>
    </MainContainer>
  );
};

export default Permission;
